import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { useAuth } from "@/lib/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Swords, Shield, CheckCircle2, AlertCircle, Eye, EyeOff } from "lucide-react";

export default function Register() {
  const navigate = useNavigate();
  const { register: registerUser, isAuthenticated } = useAuth();

  // account table fields
  const [username, setUsername]               = useState('');
  const [email, setEmail]                     = useState('');
  const [password, setPassword]               = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [birthdate, setBirthdate]             = useState('');

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');
  const [success, setSuccess]           = useState(false);

  if (isAuthenticated) {
    navigate(createPageUrl("Dashboard"));
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) return setError('Passwords do not match.');
    if (password.length < 8)          return setError('Password must be at least 8 characters.');

    setLoading(true);
    try {
      await registerUser({ username, email, password, birthdate: birthdate || null });
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative">
      {/* Background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#1a0a1a]/95 to-[#0a0a0a]" />
        <div className="absolute inset-0 bg-[url('https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/6915ad56fe64ad9662a36d0b/5e14522dd_loading_screen_01.png')] bg-cover bg-center opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/80 via-transparent to-[#0a0a0a]/90" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,#0a0a0a_100%)]" />
      </div>

      <div className="relative z-10 py-24 flex items-center justify-center min-h-screen">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">

            {/* Left — Branding */}
            <div className="text-center lg:text-left">
              <div className="flex items-center gap-3 justify-center lg:justify-start mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-[#8B0000] to-[#B22222] rounded-lg flex items-center justify-center">
                  <Swords className="w-10 h-10 text-white" />
                </div>
                <span className="text-3xl font-bold text-white">Knight Arena</span>
              </div>

              <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                Join the <span className="text-white">Alpha</span>
              </h1>
              <p className="text-xl text-gray-400 mb-8">
                From nothing to legend. Prove yourself in brutal PvP combat.
              </p>

              <div className="space-y-4 max-w-md mx-auto lg:mx-0">
                {[
                  { title: '100% Free to Play', sub: 'No pay-to-win. Ever.' },
                  { title: 'PvP-Focused Combat', sub: 'Every fight matters' },
                  { title: 'Independent Studio', sub: 'No shareholders, just passion' },
                ].map(({ title, sub }) => (
                  <div key={title} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-white mt-1 flex-shrink-0" />
                    <div className="text-left">
                      <h3 className="font-semibold text-white">{title}</h3>
                      <p className="text-gray-400 text-sm">{sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — Form */}
            <Card className="bg-gradient-to-b from-[#1a1a1a]/80 to-[#0a0a0a]/80 backdrop-blur-sm border-[#8B0000]/30 p-8">

              {success ? (
                <div className="text-center py-12">
                  <Shield className="w-16 h-16 text-white mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-white mb-2">Welcome, Warlord!</h3>
                  <p className="text-gray-400 mb-6">Your account has been created. You are now signed in.</p>
                  <Button
                    onClick={() => navigate(createPageUrl("Dashboard"))}
                    className="bg-gradient-to-r from-[#8B0000] to-[#B22222] hover:from-[#B22222] hover:to-[#8B0000] text-white hover:text-white border-none"
                  >
                    Go to Dashboard
                  </Button>
                </div>
              ) : (
                <>
                  <div className="mb-6">
                    <h2 className="text-2xl font-bold text-white mb-1">Create Your Account</h2>
                    <p className="text-gray-400 text-sm">
                      Already have one?{' '}
                      <Link to={createPageUrl("Login")} className="text-white hover:underline">Sign in</Link>
                    </p>
                  </div>

                  {error && (
                    <Alert variant="destructive" className="mb-5 bg-red-900/20 border-red-500/50">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-4">

                    {/* username */}
                    <div className="space-y-1">
                      <Label htmlFor="username" className="text-gray-300">Username</Label>
                      <Input
                        id="username"
                        type="text"
                        autoComplete="username"
                        placeholder="DarkKnight99"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        required
                        className="bg-white/5 border-[#8B0000]/40 text-white placeholder:text-gray-600 focus:border-white/50"
                      />
                      <p className="text-xs text-gray-500">3–50 characters, letters, numbers and underscores only</p>
                    </div>

                    {/* email */}
                    <div className="space-y-1">
                      <Label htmlFor="email" className="text-gray-300">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        autoComplete="email"
                        placeholder="warlord@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="bg-white/5 border-[#8B0000]/40 text-white placeholder:text-gray-600 focus:border-white/50"
                      />
                    </div>

                    {/* birthdate */}
                    <div className="space-y-1">
                      <Label htmlFor="birthdate" className="text-gray-300">
                        Date of Birth <span className="text-gray-500 font-normal">(optional)</span>
                      </Label>
                      <Input
                        id="birthdate"
                        type="date"
                        value={birthdate}
                        onChange={(e) => setBirthdate(e.target.value)}
                        max={new Date().toISOString().split('T')[0]}
                        className="bg-white/5 border-[#8B0000]/40 text-white focus:border-white/50 [color-scheme:dark]"
                      />
                    </div>

                    {/* password */}
                    <div className="space-y-1">
                      <Label htmlFor="password" className="text-gray-300">Password</Label>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          autoComplete="new-password"
                          placeholder="At least 8 characters"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          className="bg-white/5 border-[#8B0000]/40 text-white placeholder:text-gray-600 focus:border-white/50 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    {/* confirm password */}
                    <div className="space-y-1">
                      <Label htmlFor="confirmPassword" className="text-gray-300">Confirm Password</Label>
                      <Input
                        id="confirmPassword"
                        type={showPassword ? "text" : "password"}
                        autoComplete="new-password"
                        placeholder="Repeat your password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        className="bg-white/5 border-[#8B0000]/40 text-white placeholder:text-gray-600 focus:border-white/50"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-[#8B0000] to-[#B22222] hover:from-[#B22222] hover:to-[#8B0000] text-white hover:text-white py-6 text-lg border-none mt-2"
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                          Creating account...
                        </span>
                      ) : 'Create Account'}
                    </Button>
                  </form>

                  <p className="mt-6 text-xs text-gray-500 text-center">
                    By creating an account you agree to our Terms of Service.
                    <br />Knight Arena is currently in development. Alpha access is rolling.
                  </p>
                </>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
